import type { Order, OrderItem, OrderStatusKey, RoastKey } from '@c214/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch } from '../lib/api';
import { useAuth } from './useAuth';

export interface AdminOrderItem extends OrderItem {
  roast: RoastKey | null;
}

export interface AdminOrder extends Omit<Order, 'items'> {
  userEmail: string;
  items: AdminOrderItem[];
}

export interface AdminOrdersResult {
  orders: AdminOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export const ADMIN_ORDERS_QUERY_KEY = ['admin', 'orders'] as const;

export function useAdminOrders(page: number) {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: [...ADMIN_ORDERS_QUERY_KEY, page] as const,
    queryFn: () => apiGet<AdminOrdersResult>(`/api/admin/orders?page=${page}`),
    enabled: user?.role === 'admin',
  });
}

// Усі замовлення одного користувача. Викликається лише з панелі, яка
// монтується при розгортанні рядка, тож дані вантажаться тільки на вимогу.
// Ключ під префіксом ['admin','orders'], щоб оновлюватись разом з рештою.
export function useAdminUserOrders(userId: number) {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: [...ADMIN_ORDERS_QUERY_KEY, 'user', userId] as const,
    queryFn: async () =>
      (await apiGet<{ orders: AdminOrder[] }>(`/api/admin/users/${userId}/orders`)).orders,
    enabled: user?.role === 'admin',
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      ttn,
    }: {
      id: number;
      status: OrderStatusKey;
      ttn?: string | null;
    }) => apiPatch<{ order: Order }>(`/api/admin/orders/${id}/status`, { status, ttn }),
    // Увесь префікс ['admin'] — зміна статусу впливає і на списки замовлень,
    // і на лічильники/фільтр за статусом на сторінці «Користувачі».
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}
